import React, { useId } from 'react';

export interface ValveSvgProps {
  isOpen: boolean;
  color?: string | undefined; // Valve body color (default: brass '#d97706')
  voltage?: string | undefined; // e.g. '24V DC' (default), '230V AC', '12V DC'
  actualFlow?: number | undefined; // Actual flow rate (L/min)
  valveType?: 'NC' | 'NO' | string | undefined; // Normally Closed / Open
  className?: string | undefined;
  style?: React.CSSProperties | undefined;
}

export const ValveSvg: React.FC<ValveSvgProps> = ({
  isOpen,
  color = '#d97706',
  voltage = '24V DC',
  actualFlow = 0,
  valveType = 'NC',
  className,
  style,
}) => {
  const uid = useId().replace(/:/g, '_');
  const flowSpeed = actualFlow > 0 ? Math.max(0.35, 1.2 / Math.max(0.2, actualFlow / 50)) : 0.8;

  // Determine body color palette (default rich brass or custom stainless/coated)
  const isBrass = !color || color.toLowerCase() === '#d97706' || color.toLowerCase() === '#b45309';
  const bodyBase = isBrass ? '#b45309' : color;
  const bodyDark = isBrass ? '#78350f' : '#1e293b';
  const bodyDeep = isBrass ? '#451a03' : '#0f172a';
  const bodyLight = isBrass ? '#f59e0b' : '#94a3b8';
  const bodyHighlight = isBrass ? '#fef3c7' : '#ffffff';

  return (
    <g className={className} style={style}>
      <defs>
        {/* Valve Body 3D Cast Brass / Metal Gradient */}
        <linearGradient id={`valveBodyGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={bodyLight} />
          <stop offset="25%" stopColor={bodyBase} />
          <stop offset="60%" stopColor={bodyDark} />
          <stop offset="100%" stopColor={bodyDeep} />
        </linearGradient>

        {/* Hex Port Horizontal Sheen Gradient */}
        <linearGradient id={`valveHexGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={bodyLight} />
          <stop offset="20%" stopColor={bodyHighlight} stopOpacity="0.8" />
          <stop offset="50%" stopColor={bodyBase} />
          <stop offset="85%" stopColor={bodyDark} />
          <stop offset="100%" stopColor={bodyDeep} />
        </linearGradient>

        {/* Valve Belly Cylindrical Contour Gradient */}
        <linearGradient id={`valveBellyGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={bodyDeep} />
          <stop offset="20%" stopColor={bodyBase} />
          <stop offset="45%" stopColor={bodyHighlight} stopOpacity="0.5" />
          <stop offset="70%" stopColor={bodyBase} />
          <stop offset="100%" stopColor={bodyDark} />
        </linearGradient>

        {/* Solenoid Coil Housing 3D Cylindrical Gradient */}
        <linearGradient id={`solenoidCoilGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#090d16" />
          <stop offset="15%" stopColor="#1e293b" />
          <stop offset="38%" stopColor="#475569" />
          <stop offset="60%" stopColor="#1e293b" />
          <stop offset="90%" stopColor="#0f172a" />
          <stop offset="100%" stopColor="#020617" />
        </linearGradient>

        {/* Stainless Steel Armature Tube & Nut Gradient */}
        <linearGradient id={`solenoidSteelGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="30%" stopColor="#cbd5e1" />
          <stop offset="55%" stopColor="#ffffff" />
          <stop offset="75%" stopColor="#94a3b8" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>

        {/* DIN 43650 Connector Plug Gradient */}
        <linearGradient id={`dinPlugGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="45%" stopColor="#1e293b" />
          <stop offset="100%" stopColor="#090d16" />
        </linearGradient>

        {/* Flowing Fluid Passage Gradient */}
        <linearGradient id={`fluidFlowGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(56, 189, 248, 0.4)" />
          <stop offset="50%" stopColor="rgba(14, 165, 233, 0.95)" />
          <stop offset="100%" stopColor="rgba(56, 189, 248, 0.5)" />
        </linearGradient>

        {/* DIN Plug LED Glow Filter */}
        <filter id={`ledGlowFilter-${uid}`} x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="0" stdDeviation="1.8" floodColor="#22c55e" floodOpacity="0.9" />
        </filter>

        {/* Coil Electromagnetic Flux Glow Filter */}
        <filter id={`fluxGlowFilter-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="0" stdDeviation="1.5" floodColor="#38bdf8" floodOpacity="0.75" />
        </filter>
      </defs>

      <style>{`
        @keyframes valve-flow-dash-${uid} {
          0% { stroke-dashoffset: 24; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes valve-flux-pulse-${uid} {
          0%, 100% { opacity: 0.35; }
          50% { opacity: 0.85; }
        }
        @keyframes valve-led-breathe-${uid} {
          0%, 100% { opacity: 0.75; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.15); }
        }
        .valve-flow-active-${uid} {
          animation: valve-flow-dash-${uid} ${flowSpeed}s linear infinite;
        }
        .valve-flux-active-${uid} {
          animation: valve-flux-pulse-${uid} 1.6s ease-in-out infinite;
        }
      `}</style>

      {/* --- GROUND CAST SHADOW --- */}
      <ellipse cx="50" cy="81" rx="42" ry="4.5" fill="rgba(0, 0, 0, 0.38)" />

      {/* --- INLET & OUTLET HEXAGONAL PIPE PORTS (Horizontal Centerline y = 50) --- */}
      {/* Left Pipe Connection (Inlet: x = 0 to 22) */}
      <g data-testid="valve-inlet-port">
        {/* Hex Fitting Boss */}
        <rect
          x="0"
          y="42.5"
          width="16"
          height="15"
          rx="1.5"
          fill={`url(#valveHexGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="0.9"
        />
        {/* Upper/Lower Hex Facet Ridge Lines */}
        <line x1="0" y1="46" x2="16" y2="46" stroke="rgba(255, 255, 255, 0.45)" strokeWidth="0.7" />
        <line x1="0" y1="54" x2="16" y2="54" stroke="rgba(0, 0, 0, 0.4)" strokeWidth="0.7" />
        {/* Inner Pipe Throat Collar */}
        <rect
          x="16"
          y="44.5"
          width="6"
          height="11"
          fill={`url(#valveBodyGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="0.8"
        />
        {/* Left Pipe Orifice (Inlet Port Center at x = 0, y = 50) */}
        <ellipse cx="1.2" cy="50" rx="1.4" ry="4.5" fill="#090d16" stroke="#334155" strokeWidth="0.6" />
        <ellipse cx="1.2" cy="50" rx="0.9" ry="3" fill="#020617" />
      </g>

      {/* Right Pipe Connection (Outlet: x = 78 to 100) */}
      <g data-testid="valve-outlet-port">
        {/* Inner Pipe Throat Collar */}
        <rect
          x="78"
          y="44.5"
          width="6"
          height="11"
          fill={`url(#valveBodyGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="0.8"
        />
        {/* Hex Fitting Boss */}
        <rect
          x="84"
          y="42.5"
          width="16"
          height="15"
          rx="1.5"
          fill={`url(#valveHexGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="0.9"
        />
        {/* Upper/Lower Hex Facet Ridge Lines */}
        <line x1="84" y1="46" x2="100" y2="46" stroke="rgba(255, 255, 255, 0.45)" strokeWidth="0.7" />
        <line x1="84" y1="54" x2="100" y2="54" stroke="rgba(0, 0, 0, 0.4)" strokeWidth="0.7" />
        {/* Right Pipe Orifice (Outlet Port Center at x = 100, y = 50) */}
        <ellipse cx="98.8" cy="50" rx="1.4" ry="4.5" fill="#090d16" stroke="#334155" strokeWidth="0.6" />
        <ellipse cx="98.8" cy="50" rx="0.9" ry="3" fill="#020617" />
      </g>

      {/* --- VALVE MAIN CASTING BODY (Forged Brass / Alloy Belly) --- */}
      <g data-testid="valve-body-casting">
        {/* Main Volumetric Bowl Housing */}
        <path
          d="M 22 41 L 31 37 L 69 37 L 78 41 L 78 58 C 78 73 66 79 50 79 C 34 79 22 73 22 58 Z"
          fill={`url(#valveBellyGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="1.2"
        />

        {/* Specular 3D Highlight Arch on Upper Belly */}
        <path
          d="M 27 45 Q 50 39 73 45"
          fill="none"
          stroke="rgba(255, 255, 255, 0.55)"
          strokeWidth="1.2"
          strokeLinecap="round"
        />

        {/* Sump / Mounting Base Boss */}
        <rect
          x="41"
          y="77.5"
          width="18"
          height="4.5"
          rx="1.5"
          fill={bodyDark}
          stroke="#0f172a"
          strokeWidth="0.8"
        />
        {/* Center Mounting Screw Hole */}
        <circle cx="50" cy="79.5" r="1.3" fill="#090d16" stroke="#1e293b" strokeWidth="0.5" />

        {/* Flow Direction Arrow (Akış Yönü Oku) - 3D Embossed Relief */}
        <g data-testid="valve-flow-arrow">
          {/* Arrow Under-Shadow */}
          <path
            d="M 41 62.5 L 51 62.5 L 51 59.5 L 59 63.5 L 51 67.5 L 51 64.5 L 41 64.5 Z"
            fill={bodyDeep}
          />
          {/* Arrow Raised Body */}
          <path
            d="M 41 61.5 L 51 61.5 L 51 58.5 L 59 62.5 L 51 66.5 L 51 63.5 L 41 63.5 Z"
            fill={bodyLight}
            stroke={bodyHighlight}
            strokeWidth="0.4"
          />
        </g>

        {/* Forged Casting Specifications Text */}
        <text
          x="50"
          y="73"
          fill="rgba(0, 0, 0, 0.55)"
          fontSize="3.4"
          fontWeight="bold"
          textAnchor="middle"
          letterSpacing="0.4"
          fontFamily="monospace"
        >
          PN16 • G½
        </text>
      </g>

      {/* --- FLUID FLOW INTERNAL CHANNEL & FLOW ANIMATION --- */}
      <g data-testid="valve-flow-passage">
        {/* Inner Passage Cavity Bed */}
        <rect
          x="6"
          y="46"
          width="88"
          height="8"
          rx="2"
          fill="#090d16"
          stroke="#1e293b"
          strokeWidth="0.8"
        />

        {isOpen ? (
          /* Active Flow Stream (When Solenoid is Open) */
          <g data-testid="valve-flow-active">
            {/* Liquid Stream Backfill */}
            <rect
              x="6"
              y="46.5"
              width="88"
              height="7"
              rx="1.5"
              fill={`url(#fluidFlowGrad-${uid})`}
            />

            {/* Dynamic Animated Flow Stream Chevrons */}
            <line
              x1="8"
              y1="50"
              x2="92"
              y2="50"
              stroke="#e0f2fe"
              strokeWidth="2.2"
              strokeDasharray="6,6"
              strokeLinecap="round"
              className={`valve-flow-active-${uid}`}
            />
            {/* Speed Stream Center Sheen */}
            <line
              x1="12"
              y1="48.5"
              x2="88"
              y2="48.5"
              stroke="rgba(255, 255, 255, 0.7)"
              strokeWidth="0.8"
            />
          </g>
        ) : (
          /* Blocked State (When Solenoid is Closed) */
          <g data-testid="valve-flow-closed">
            {/* Stagnant fluid at inlet up to plunger seal */}
            <rect
              x="6"
              y="46.5"
              width="41"
              height="7"
              rx="1.5"
              fill={`url(#fluidFlowGrad-${uid})`}
              opacity="0.3"
            />
            {/* Dark Rubber Viton/EPDM Valve Seal Obstruction */}
            <rect
              x="47"
              y="44"
              width="6"
              height="12"
              rx="1"
              fill="#0f172a"
              stroke="#334155"
              strokeWidth="0.6"
            />
            {/* Red Closed Seal Marker Bar */}
            <line x1="50" y1="45" x2="50" y2="55" stroke="#ef4444" strokeWidth="1.2" strokeLinecap="round" />
          </g>
        )}
      </g>

      {/* --- VALVE BONNET (Gövde-Kapak Bağlantı Flanşı & Cıvatalar) --- */}
      <g data-testid="valve-bonnet">
        {/* Hexagonal Bonnet Plate */}
        <rect
          x="28"
          y="34"
          width="44"
          height="6"
          rx="1.5"
          fill={`url(#valveHexGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="1"
        />
        {/* Bonnet Plate Specular Edge */}
        <line x1="30" y1="35.5" x2="70" y2="35.5" stroke="rgba(255, 255, 255, 0.6)" strokeWidth="0.8" />

        {/* 4 Heavy-Duty Bonnet Hex Screws */}
        <circle cx="33" cy="37" r="1.6" fill="#f8fafc" stroke="#334155" strokeWidth="0.5" />
        <circle cx="33" cy="37" r="0.7" fill="#0f172a" />

        <circle cx="44" cy="37" r="1.6" fill="#f8fafc" stroke="#334155" strokeWidth="0.5" />
        <circle cx="44" cy="37" r="0.7" fill="#0f172a" />

        <circle cx="56" cy="37" r="1.6" fill="#f8fafc" stroke="#334155" strokeWidth="0.5" />
        <circle cx="56" cy="37" r="0.7" fill="#0f172a" />

        <circle cx="67" cy="37" r="1.6" fill="#f8fafc" stroke="#334155" strokeWidth="0.5" />
        <circle cx="67" cy="37" r="0.7" fill="#0f172a" />
      </g>

      {/* --- STAINLESS STEEL ARMATURE GUIDE TUBE (Armatür Kovanı & Planjör) --- */}
      <g data-testid="valve-armature-stem">
        {/* Armature Sleeve Tube */}
        <rect
          x="43.5"
          y="27"
          width="13"
          height="8"
          rx="1"
          fill={`url(#solenoidSteelGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="0.9"
        />
        {/* Dynamic Plunger Indicator (Lifts when Open) */}
        <g
          data-testid="valve-plunger"
          style={{
            transform: isOpen ? 'translateY(-3.5px)' : 'translateY(0)',
            transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          <rect x="46" y="28" width="8" height="5" rx="0.8" fill="#334155" stroke="#0f172a" strokeWidth="0.5" />
          <line x1="47" y1="30.5" x2="53" y2="30.5" stroke="#cbd5e1" strokeWidth="0.6" />
        </g>

        {/* Manual Override Test Spindle (Manuel Müdahale Butonu/Vidası) */}
        <circle cx="50" cy="31" r="2.2" fill="#eab308" stroke="#78350f" strokeWidth="0.6" />
        <line x1="48.5" y1="31" x2="51.5" y2="31" stroke="#451a03" strokeWidth="0.7" />
      </g>

      {/* --- SOLENOID ACTUATOR COIL (Solenoid Bobini & Manyetik Kutu) --- */}
      <g data-testid="valve-solenoid-coil">
        {/* Electromagnetic Flux Field Pulse (When Energized) */}
        {isOpen && (
          <g className={`valve-flux-active-${uid}`} data-testid="valve-magnetic-flux">
            <path
              d="M 25 12 C 22 17 22 23 25 28"
              fill="none"
              stroke="#38bdf8"
              strokeWidth="1.2"
              strokeDasharray="2,2"
              filter={`url(#fluxGlowFilter-${uid})`}
            />
            <path
              d="M 23 8 C 18 16 18 24 23 32"
              fill="none"
              stroke="#38bdf8"
              strokeWidth="0.9"
              opacity="0.6"
              strokeDasharray="3,2"
            />
          </g>
        )}

        {/* Coil Molded Thermoplastic Enclosure */}
        <rect
          x="28"
          y="6"
          width="40"
          height="28"
          rx="3.5"
          fill={`url(#solenoidCoilGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="1.2"
        />

        {/* Left Side Heat-Dissipation Vertical Grooves */}
        <line x1="31.5" y1="9" x2="31.5" y2="31" stroke="#0f172a" strokeWidth="1" />
        <line x1="32" y1="9" x2="32" y2="31" stroke="rgba(255, 255, 255, 0.25)" strokeWidth="0.5" />
        <line x1="34.5" y1="9" x2="34.5" y2="31" stroke="#0f172a" strokeWidth="1" />
        <line x1="35" y1="9" x2="35" y2="31" stroke="rgba(255, 255, 255, 0.25)" strokeWidth="0.5" />

        {/* Top Chamfer Edge Highlight */}
        <path
          d="M 31 7.5 L 65 7.5"
          stroke="rgba(255, 255, 255, 0.35)"
          strokeWidth="0.8"
          strokeLinecap="round"
        />

        {/* Solenoid Specification Nameplate (Alüminyum Etiket) */}
        <g data-testid="valve-nameplate">
          <rect
            x="37"
            y="9"
            width="28"
            height="22"
            rx="1.5"
            fill="#090d16"
            stroke="#334155"
            strokeWidth="0.7"
          />

          {/* Coil Electrical Schematic Symbol (Kutu & Çapraz Ok) */}
          <rect x="39" y="11" width="5.5" height="4" fill="none" stroke="#38bdf8" strokeWidth="0.6" />
          <line x1="38" y1="16" x2="45.5" y2="10" stroke="#38bdf8" strokeWidth="0.6" />

          {/* "SOLENOID" Brand Header */}
          <text
            x="46"
            y="14"
            fill="#38bdf8"
            fontSize="3"
            fontWeight="bold"
            letterSpacing="0.4"
          >
            SOLENOID
          </text>

          {/* Operating Voltage Badge */}
          <text
            x="51"
            y="21"
            fill="#f8fafc"
            fontSize="4.4"
            fontWeight="900"
            textAnchor="middle"
            letterSpacing="0.5"
          >
            {voltage}
          </text>

          {/* Specification Subtext */}
          <text
            x="51"
            y="27.5"
            fill="#94a3b8"
            fontSize="2.7"
            fontWeight="600"
            textAnchor="middle"
            letterSpacing="0.3"
          >
            {valveType} • IP65
          </text>
        </g>
      </g>

      {/* --- TOP KNURLED RETAINING NUT & WASHER (Üst Sabitleme Somunu) --- */}
      <g data-testid="valve-top-nut">
        {/* Retaining Nut Body */}
        <rect
          x="43"
          y="2"
          width="14"
          height="5"
          rx="1"
          fill={`url(#solenoidSteelGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="0.8"
        />
        {/* Knurled Grip Texture Lines */}
        <line x1="45" y1="2.5" x2="45" y2="6.5" stroke="#1e293b" strokeWidth="0.6" />
        <line x1="47" y1="2.5" x2="47" y2="6.5" stroke="#1e293b" strokeWidth="0.6" />
        <line x1="49" y1="2.5" x2="49" y2="6.5" stroke="#1e293b" strokeWidth="0.6" />
        <line x1="51" y1="2.5" x2="51" y2="6.5" stroke="#1e293b" strokeWidth="0.6" />
        <line x1="53" y1="2.5" x2="53" y2="6.5" stroke="#1e293b" strokeWidth="0.6" />
        <line x1="55" y1="2.5" x2="55" y2="6.5" stroke="#1e293b" strokeWidth="0.6" />

        {/* Center Retaining Stud Dome */}
        <ellipse cx="50" cy="2" rx="2.8" ry="1" fill="#cbd5e1" stroke="#0f172a" strokeWidth="0.5" />
      </g>

      {/* --- DIN 43650 CONNECTOR PLUG & ILLUMINATED LED WINDOW --- */}
      <g data-testid="valve-din-connector">
        {/* Connector Mounting Base Plate on Coil */}
        <rect
          x="65"
          y="11"
          width="4"
          height="18"
          fill="#1e293b"
          stroke="#0f172a"
          strokeWidth="0.7"
        />

        {/* Angled DIN Plug Housing */}
        <path
          d="M 68 10 L 83 10 Q 87 10 87 14 L 87 25 Q 87 28 84 28 L 68 28 Z"
          fill={`url(#dinPlugGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="1"
        />

        {/* Central Plug Screw */}
        <circle cx="73" cy="19.5" r="1.6" fill="#64748b" stroke="#0f172a" strokeWidth="0.5" />
        <line x1="71.8" y1="19.5" x2="74.2" y2="19.5" stroke="#1e293b" strokeWidth="0.5" />
        <line x1="73" y1="18.3" x2="73" y2="20.7" stroke="#1e293b" strokeWidth="0.5" />

        {/* PG9 Compression Cable Gland Nut */}
        <rect
          x="85"
          y="18"
          width="6"
          height="8"
          rx="1.5"
          fill="#334155"
          stroke="#0f172a"
          strokeWidth="0.8"
        />
        {/* Gland Hex Facet Ridge */}
        <line x1="87.5" y1="18.5" x2="87.5" y2="25.5" stroke="#475569" strokeWidth="0.6" />

        {/* Flexible Rubber Power Supply Cable */}
        <path
          d="M 91 22 Q 97 22 97 28 L 97 40"
          fill="none"
          stroke="#090d16"
          strokeWidth="3.2"
          strokeLinecap="round"
        />
        <path
          d="M 91 22 Q 97 22 97 28 L 97 40"
          fill="none"
          stroke="#334155"
          strokeWidth="0.8"
          strokeLinecap="round"
        />

        {/* --- ILLUMINATED STATUS LED WINDOW (Işıklı Soket Başlığı) --- */}
        <g data-testid="valve-led-window">
          {/* Transparent Polycarbonate Beveled Frame */}
          <rect
            x="75.5"
            y="12.5"
            width="9.5"
            height="6"
            rx="1.2"
            fill="#090d16"
            stroke="#334155"
            strokeWidth="0.6"
          />

          {isOpen ? (
            /* Luminous Green Active State (Energized) */
            <g data-testid="valve-led-on">
              <rect
                x="76.5"
                y="13.5"
                width="7.5"
                height="4"
                rx="0.9"
                fill="#22c55e"
                filter={`url(#ledGlowFilter-${uid})`}
              />
              {/* LED Halo Ring */}
              <circle
                cx="80.2"
                cy="15.5"
                r="3.5"
                fill="none"
                stroke="#4ade80"
                strokeWidth="0.6"
                opacity="0.8"
              />
              {/* "ON" Indicator Text */}
              <text
                x="80.2"
                y="16.5"
                fill="#ffffff"
                fontSize="2.8"
                fontWeight="bold"
                textAnchor="middle"
              >
                ON
              </text>
            </g>
          ) : (
            /* Dark De-energized State (Closed) */
            <g data-testid="valve-led-off">
              <rect
                x="76.5"
                y="13.5"
                width="7.5"
                height="4"
                rx="0.9"
                fill="#1e293b"
              />
              <circle cx="80.2" cy="15.5" r="1.2" fill="#475569" />
              {/* "OFF" Indicator Text */}
              <text
                x="80.2"
                y="16.5"
                fill="#64748b"
                fontSize="2.8"
                fontWeight="bold"
                textAnchor="middle"
              >
                OFF
              </text>
            </g>
          )}
        </g>
      </g>
    </g>
  );
};
